import { FC, useMemo, useState, useEffect, ReactNode, Key, CSSProperties } from 'react';
// 组件
import {
  ProSelect,
  ProDatePicker,
} from '../antd';
import SearchFormItem from './subpage/searchFormItem';
import SearchFormDisabled from './subpage/disabled';
import FormItem from './subpage/formItem';
// 方法
import { useLayoutContext } from '../PageLayout/context/LayoutContext';
// 常量
import { FormItemProps } from 'antd/lib/form';
import { SelectProps, DefaultOptionType } from 'antd/lib/select';
import { DatePickerProps, RangePickerProps } from 'antd/lib/date-picker';
import {
  BaseSearchFormItemProps,
  SearchFormItemEnum,
} from './interface/baseInterface';
import { baseRanges } from './constant';
import { NodeKey } from '../constants/interface';

export interface ZoneTimeSelectProps {
  /** 时区字典code */
  enumCode?: string;
  /** 时区选项配置 */
  options?: DefaultOptionType[];
  /** 默认时区 */
  defaultValue?: string;
  /** 时区下拉配置 */
  selectProps?: SelectProps;
  /** 时区宽度flex比重 */
  flex?: number;
}
export interface TimeOptions {
  timeType:
    | 'DatePicker'
    | 'RangePicker';
  label?: ReactNode;
  value?: NodeKey;
  disabled?: boolean;
  datePickerProps?: DatePickerProps;
  rangePickerProps?: RangePickerProps;
}
export interface ZoneTimeProps {
  /** 默认时区类型 */
  defaultValue?: string;
  /** before表单配置 */
  beforeFormItemProps?: FormItemProps;
  /** after表单配置 */
  afterFormItemProps?: FormItemProps;
  /** 时间类型选项 */
  options?: TimeOptions[]
  /** 下拉框配置 */
  selectProps?: SelectProps;
  /** 时间类型flex比重 */
  beforeFlex?: number;
  /** 时间flex比重 */
  afterFlex?: number;
}
export interface SearchFormZoneTimeProps extends BaseSearchFormItemProps {
  /** 是否禁用 */
  disabled?: boolean;
  /** 时区配置 */
  zoneProps?: ZoneTimeSelectProps;
  /** 下拉选项 */
  zoneTimeProps?: ZoneTimeProps;
}

const formItemStyle: CSSProperties = {
  display: 'inline-block',
  marginBottom: 0,
  marginRight: 0,
};
const fullStyle: CSSProperties = { width: '100%' };
const SearchFormZoneTime: FC<SearchFormZoneTimeProps> = props => {
  const { isDetail, disabled, form, isTable, zoneProps, zoneTimeProps, label, initialValue, name, render, disabledRender, ...restProps } = props;
  const [selectValue, setSelectValue] = useState<Key | undefined>(
    Array.isArray(initialValue) ? (zoneProps ? initialValue?.[1] : initialValue?.[0]) : zoneTimeProps?.options?.[0]?.value
  );
  const { enumData } = useLayoutContext();
  const zoneName = Array.isArray(name) && zoneProps ? name[0] : '';
  const timeTypeName = Array.isArray(name) && zoneTimeProps ? zoneProps ? name[1] : name[0] : '';
  const timeName = Array.isArray(name) && zoneTimeProps ? zoneProps ? name[2] : name[1] : '';
  useEffect(() => {
    if (Array.isArray(name) && form) {
      const initValues: any = {};
      if (Array.isArray(initialValue)) {
        name.forEach((item, index) => {
          initValues[item] = initialValue[index];
        });
      } else {
        if (timeTypeName) {
          initValues[timeTypeName] = zoneTimeProps?.options?.[0]?.value;
        }
      }
      
      form.setFieldsValue(initValues);
    }
  }, []);
  const newZoneOptions: any[] | undefined = useMemo(() => {
    if (!zoneProps) return;
    const { options, enumCode } = zoneProps;
    if (options) return options;
    if (enumCode && enumData) return enumData[enumCode]?.items;
  }, [zoneProps?.enumCode, zoneProps?.options]);
  const zoneTimeOptions = useMemo(() => {
    return zoneTimeProps?.options?.map(item => ({
      label: item.label,
      value: item.value,
      disabled: item.disabled,
    }));
  }, [zoneTimeProps]);
  const handleChange = (value: any, option: any) => {
    const prevItem = zoneTimeProps?.options?.find(item => item.value === selectValue);
    const nextItem = zoneTimeProps?.options?.find(item => item.value === value);
    setSelectValue(value);
    if (prevItem?.timeType !== nextItem?.timeType && form && timeName) {
      form.setFieldsValue({ [timeName]: undefined });
    }
  };
  const afterHTML = useMemo(() => {
    const findOption = zoneTimeProps?.options?.find(item => item.value === selectValue);
    if (findOption) {
      const { datePickerProps, rangePickerProps, timeType, } = findOption;
      switch (timeType) {
        case 'DatePicker':
          return <ProDatePicker className="yb-zone-time-datePicker" style={fullStyle} {...datePickerProps} />;
        case 'RangePicker':
          return (
            <ProDatePicker.RangePicker
              className="yb-zone-time-rangePicker"
              style={fullStyle}
              ranges={baseRanges}
              {...rangePickerProps}
            />
          );
      }
    }
  }, [selectValue]);
  return (
    <SearchFormItem label={label} {...restProps}>
      {isDetail || disabled ? (
        <SearchFormDisabled render={render || disabledRender} form={form} isTable={isTable} />
      ) : (
        <div className="yb-zone-time">
          {
            !!zoneProps && (
              <FormItem
                style={{...formItemStyle, flex: zoneProps.flex || 1}}
                name={zoneName}
                initialValue={initialValue?.[0]}
              >
                <ProSelect
                  options={newZoneOptions}
                  defaultValue={zoneProps.defaultValue}
                  placeholder="时间类型"
                  className="yb-zone-time-zone"
                  {...zoneProps.selectProps}
                />
              </FormItem>
            )
          }
          {
            Array.isArray(zoneTimeProps?.options) && (
              <>
                <FormItem
                  style={{...formItemStyle, flex: zoneTimeProps?.beforeFlex || 1}}
                  name={timeTypeName}
                  initialValue={zoneProps ? initialValue?.[1] : initialValue?.[0]}
                >
                  <ProSelect
                    options={zoneTimeOptions}
                    defaultValue={zoneTimeProps?.defaultValue}
                    className="yb-zone-time-type"
                    onChange={handleChange}
                    {...zoneTimeProps?.selectProps}
                  />
                </FormItem>
                <FormItem
                  style={{...formItemStyle, flex: zoneTimeProps?.afterFlex || 1}}
                  name={timeName}
                  initialValue={zoneProps ? initialValue?.[2] : initialValue?.[1]}
                >
                  {afterHTML}
                </FormItem>
              </>
            )
          }
        </div>
      )}
    </SearchFormItem>
  );
};

SearchFormZoneTime.displayName = SearchFormItemEnum.SearchFormZoneTime;

export default SearchFormZoneTime;
